import type { FormKitNode } from "@formkit/core";

import { en, fr } from "@formkit/i18n";
import { generateClasses } from "@formkit/themes";
import { createInput, defaultConfig } from "@formkit/vue";

import { useTypedIntl } from "@/locales/intl";

import AddressAutocomplete from "@/components/address/address-autocomplete.vue";
import MinYearDropdown from "@/components/common/min-year-dropdown.vue";
import HeatpumpBrandAutocomplete from "@/components/heatpump/heatpump-brand-autocomplete.vue";
import HeatpumpModelAutocomplete from "@/components/heatpump/heatpump-model-autocomplete.vue";
import MissionDropdown from "@/components/mission/mission-dropdown.vue";
import OperationCascadeSelect from "@/components/operation/operation-cascade-select.vue";
import NetworkManagerDropdown from "@/components/site/network-manager-dropdown.vue";
import SiteLayoutCascadeSelect from "@/components/site/site-layout-cascade-select.vue";
import SiteOccupancyTypeDropdown from "@/components/site/site-occupancy-type-dropdown.vue";
import SubscriberPowerTypeDropdown from "@/components/subscriber/subscriber-power-type-dropdown.vue";
import Dropdown from "@/components/ui/input/dropdown/dropdown.vue";
import NumberInput from "@/components/ui/input/number-input/number-input.vue";
import TextInput from "@/components/ui/input/text-input/text-input.vue";

const legends = ["checkbox_multi", "radio_multi", "repeater", "transferlist"];

function addOptionalLabelPlugin(node: FormKitNode) {
  if (["button", "submit", "hidden", "group", "list", "meta"].includes(node.props.type)) {
    return;
  }
  const { t, k } = useTypedIntl();

  node.on("created", () => {
    const legendOrLabel = legends.includes(`${node.props.type}${node.props.options ? "_multi" : ""}`)
      ? "legend"
      : "label";

    if (node.props.definition && typeof node.props.definition.schema === "function") {
      const originalSchemaFn = node.props.definition.schema;

      node.props.definition.schema = (sectionsSchema = {}) => {
        sectionsSchema[legendOrLabel] = {
          children: [
            "$label",
            {
              $el: "small",
              if: "$state.required === false",
              children: [` (${t(k.global.optional)})`],
            },
          ],
        };

        return originalSchemaFn(sectionsSchema);
      };
    }
  });
}

function scrollToErrors(node: FormKitNode) {
  if (node.props.type === "form") {
    function scrollTo(node: FormKitNode) {
      const el = document.getElementById(node.props.id!);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
      }
    }

    function scrollToErrors() {
      node.walk((child) => {
        if (child.ledger.value("blocking") || child.ledger.value("errors")) {
          scrollTo(child);
          return false;
        }
      }, true);
    }

    const onSubmitInvalid = node.props.onSubmitInvalid;
    node.props.onSubmitInvalid = () => {
      onSubmitInvalid(node);
      scrollToErrors();
    };
    node.on("unsettled:errors", scrollToErrors);
  }
  return false;
}
export default defaultConfig({
  locales: { fr, en },
  locale: "fr",
  plugins: [addOptionalLabelPlugin, scrollToErrors],
  config: {
    classes: generateClasses({
      global: {
        inner: "mt-1",
        message: "text-destructive",
      },
      radio: {
        options: "flex gap-3 flex-wrap",
        input: "$reset p-radiobutton p-radiobutton-input h-22 w-22",
        label: "ml-1",
      },
      button: {
        input: "p-button p-component p-button-link !p-0",
      },
      text: {
        input: "$reset p-inputtext p-component w-full",
      },
      number: {
        input: "$reset p-inputtext p-component w-full",
      },
      submit: {
        wrapper: "mt-3 flex justify-end",
        input: "p-button w-fit",
      },
    }),
  },

  inputs: {
    customDropdown: createInput(Dropdown, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled", "optionLabel"],
    }),
    siteOccupancyTypeDropdown: createInput(SiteOccupancyTypeDropdown, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled"],
    }),
    customNetworkManagerDropdown: createInput(NetworkManagerDropdown, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled", "insee", "siteId"],
    }),
    minYearDropdown: createInput(MinYearDropdown, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled", "minYear"],
    }),
    customSiteLayoutCascadeSelect: createInput(SiteLayoutCascadeSelect, {
      props: ["placeholder", "class", "disabled", "includeAccommodations", "includeProfessionals"],
    }),
    operationCascadeSelect: createInput(OperationCascadeSelect, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled"],
    }),
    missionDropdown: createInput(MissionDropdown, {
      props: ["options", "optionLabel", "placeholder", "class", "disabled"],
    }),
    customTextInput: createInput(TextInput, {
      props: ["placeholder", "class", "disabled"],
    }),
    customNumberInput: createInput(NumberInput, {
      props: ["placeholder", "class", "disabled"],
    }),
    heatpumpBrandAutocomplete: createInput(HeatpumpBrandAutocomplete, {
      props: ["placeholder", "class", "disabled"],
    }),
    heatpumpModelAutocomplete: createInput(HeatpumpModelAutocomplete, {
      props: ["placeholder", "class", "disabled", "brandId"],
    }),
    addressAutocomplete: createInput(AddressAutocomplete, {
      props: ["placeholder", "class", "disabled", "types", "country"],
    }),
    subscriberPowerTypeDropdown: createInput(SubscriberPowerTypeDropdown, {
      props: ["placeholder", "class", "disabled"],
    }),
  },
});
